import React, { useState } from 'react';
import { Grid, Header, Icon, Popup } from 'semantic-ui-react';

import { ClippedContent, Editable, generateResolver, Notification, STATUS_TYPES, VALIDATORS } from 'dyl-components';
import Avatar from 'react-avatar';

import './index.scss'
import { Controller, useForm } from 'react-hook-form';
import masterAccountActions from 'actions/master_account';
import { connect, useDispatch, useSelector } from 'react-redux';

import ErrorPopup from 'shared/ErrorPopup';
import { FAVORITE_TYPE } from 'shared/constants/FAVORITE_TYPE';
import { ProfileFavoriteIcon } from 'shared/ProfileFavoriteIcon';

function copy(text) {
    return (e) => {
        e.preventDefault();
        navigator.clipboard.writeText(text);
    };
}

const mapStateToProps = state => ({
    isUpdating: state.master_account.isUpdatingMasterAccount
});

const mapDispatchToProps = dispatch => ({
    onUpdateAccount: (account_id, payload) => {
        return dispatch(masterAccountActions.update(account_id, payload));
    },
    onReadAccount: (account_id) => {
        dispatch(masterAccountActions.readOne(account_id));
    }
})

export const ProfileHeader = connect(mapStateToProps, mapDispatchToProps)(({
    name,
    id,
    type,
    breadcrumbs,

    isSidePanelOpen,
    onReadAccount,
    isUpdating,
    onUpdateAccount,

    pinnedAccounts
}) => {
    const [isEditing, setEditing] = useState(false);
    const [typingTimeout, setTypingtimeout] = useState(null);
    const dispatch = useDispatch();
    const {isCheckingDuplicateMasterAccount} = useSelector(state => state.master_account);
    const NAME_VALIDATOR = VALIDATORS.MASTER_NAME().no_whitespace_only()
    const { control, formState: { isValid, isDirty }, handleSubmit, reset, clearErrors, setError } = useForm({
        mode: 'onChange',
        defaultValues: {
            name
        },
        resolver: generateResolver({
            name: NAME_VALIDATOR.required('This field is required')
        })
    });

    const onEdit = () => {
        setEditing(true);
    }

    const onCancelEdit = () => {
        setEditing(false);
        reset({
            name: name || ''
        })
    }


    const onConfirmEdit = async (data) => {
        try {
            await onUpdateAccount(id, { ...data, pins: pinnedAccounts.map(account => account.id) });
            Notification.alert('Successfully updated account!', STATUS_TYPES.SUCCESS);
            onReadAccount(id);
        } catch {
            Notification.alert('Failed to update account', STATUS_TYPES.ERROR);
        }
    }

    return (
        <React.Fragment>
            <div style={{ paddingBottom: "0.5em" }}>
                {breadcrumbs}
            </div>
            <Grid>
                <Grid.Row columns='equal' verticalAlign='middle'>
                    <Grid.Column width={6}>
                        <Grid columns='equal' verticalAlign='middle'>
                            <Grid.Column width={isSidePanelOpen ? 4 : 3}>
                                <Avatar
                                    size='5em'
                                    round
                                    maxInitials={2}
                                    name={name} />
                            </Grid.Column>
                            <Grid.Column>
                                <Header as='h3'>
                                    {!isEditing ?
                                        <div onClick={onEdit}>
                                            <ClippedContent>
                                                {name}
                                            </ClippedContent>
                                        </div> : (
                                            <Editable
                                                value={name}
                                                isEditing={isEditing}
                                                onCancelEdit={onCancelEdit}
                                                onEdit={onEdit}
                                                size='small'
                                                underline
                                                onConfirmEdit={handleSubmit(onConfirmEdit, (errors, e) => {
                                                    console.log(errors, e);
                                                })}
                                                recordType="master_account"
                                                submitDisabled={!isDirty || !isValid || isUpdating || isCheckingDuplicateMasterAccount}
                                            >
                                                <Controller
                                                    name='name'
                                                    control={control}
                                                    render={({ field: { value, name:fieldName, onChange }, fieldState: { error } }) => (
                                                        <ErrorPopup
                                                            isPopupOpen={typeof (error?.message) != "undefined"}
                                                            message={error?.message}
                                                            inputName={fieldName}
                                                            inputValue={value}
                                                            onInputChange={async (_, { value }) => {
                                                                if (typingTimeout) {
                                                                    clearTimeout(typingTimeout)
                                                                }
                                                                onChange({ target: { name:fieldName, value } });
                                                                const isValid = await NAME_VALIDATOR.isValid(value);
                                                                if (isValid && value !== '' && value !== name) {
                                                                    setTypingtimeout(setTimeout(async () => {
                                                                        await clearErrors(fieldName);
                                                                        const duplicate = (await dispatch(masterAccountActions.checkDuplicateMasterAccount(undefined, { master_account_name: value })))?.id;
                                                                        if (duplicate) {
                                                                            await setError("name", {
                                                                                type: 'unique_master_account',
                                                                                message: 'Master account name already exists!'
                                                                            })
                                                                        }
                                                                    }, 150));
                                                                }
                                                            }}
                                                            inputPlaceholder='Enter new name'
                                                            isLoading={isUpdating}
                                                        />
                                                    )}
                                                />
                                            </Editable>
                                        )}
                                    <div className='ProfileHeader__icons'>
                                        <ProfileFavoriteIcon id={id} type={FAVORITE_TYPE.MASTER_ACCOUNT} />
                                    </div>
                                </Header>
                            </Grid.Column>
                        </Grid>
                    </Grid.Column>
                    <Grid.Column textAlign='left'>
                        <Header as='h3' color='primary' style={{ marginTop: 0, marginBottom: 0 }}>
                            <Header.Content>
                                <Icon.Group>
                                    <Icon color='primary' size='big' name='circle outline' />
                                    <Icon color='primary' className='fas fa-buildings' />
                                </Icon.Group> {type?.toUpperCase()}
                            </Header.Content>
                        </Header>
                        <span style={{ marginLeft: '0.75em' }}>
                            ID: <Popup
                                trigger={<a href="#?" role="button" tabIndex="0" onClick={copy(id)}>{id} <Icon className='fas fa-copy' /></a>}
                                content='Copied!'
                                on='click'
                                closeOnTriggerMouseLeave
                                position='bottom center'
                                inverted />
                        </span>
                    </Grid.Column>
                    <Grid.Column textAlign='right'>
                        <Icon name='fas fa-ellipsis-vertical' />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </React.Fragment>
    );
});
